// Layout do popup do chat (posição, tamanho e largura da lista de conversas),
// guardado por usuário. A chave é prefixada com o id de quem está logado
// porque duas contas no mesmo navegador herdavam o layout uma da outra —
// antes havia uma única `chatPopupSize`/`chatPopupPosition` global.

export interface ChatLayout {
  position: { x: number; y: number };
  size: { width: number; height: number };
  /** Largura em px da coluna da lista de conversas. */
  larguraDaLista: number;
  listaColapsada: boolean;
}

export const LIMITES_DO_POPUP = {
  // O mínimo de 640 é o que faz lista + conversa caberem lado a lado; abaixo
  // disso as duas colunas ficam inutilizáveis. Teto não é fixo: quem quiser o
  // chat ocupando a tela inteira deve conseguir — o limite é o viewport.
  largura: { min: 640 },
  altura: { min: 500 },
  larguraDaLista: { min: 180, fracaoMaxima: 0.4, padrao: 240 },
  // Faixa do popup que precisa continuar dentro da tela — é por ela (o header)
  // que se arrasta a janela.
  margemVisivel: 60,
} as const;

const CHAVE_BASE = 'chatPopupLayout';
const CHAVES_ANTIGAS = { size: 'chatPopupSize', position: 'chatPopupPosition' } as const;

export interface Viewport {
  width: number;
  height: number;
}

export function chaveDoLayout(userId: string | null | undefined): string {
  return `${CHAVE_BASE}:${userId || 'anon'}`;
}

const numero = (valor: unknown, padrao: number): number =>
  typeof valor === 'number' && Number.isFinite(valor) ? valor : padrao;

const limitar = (valor: number, min: number, max: number): number =>
  Math.min(Math.max(valor, min), Math.max(min, max));

/**
 * Completa o que faltou e corrige o que não cabe: valores salvos numa tela
 * maior (ou corrompidos) não podem jogar o popup fora do viewport atual.
 */
export function ajustarLayout(bruto: Partial<ChatLayout> | null | undefined, viewport: Viewport): ChatLayout {
  const { largura, altura, larguraDaLista, margemVisivel } = LIMITES_DO_POPUP;

  const larguraMaxima = Math.max(largura.min, viewport.width);
  const alturaMaxima = Math.max(altura.min, viewport.height);

  const width = limitar(numero(bruto?.size?.width, viewport.width * 0.6), largura.min, larguraMaxima);
  const height = limitar(numero(bruto?.size?.height, viewport.height * 0.75), altura.min, alturaMaxima);

  const x = limitar(numero(bruto?.position?.x, 100), 0, Math.max(0, viewport.width - width));
  const y = limitar(numero(bruto?.position?.y, 100), 0, Math.max(0, viewport.height - margemVisivel));

  const listaMaxima = width * larguraDaLista.fracaoMaxima;
  const lista = limitar(
    numero(bruto?.larguraDaLista, larguraDaLista.padrao),
    larguraDaLista.min,
    listaMaxima
  );

  return {
    position: { x, y },
    size: { width, height },
    larguraDaLista: lista,
    listaColapsada: bruto?.listaColapsada === true,
  };
}

const lerJson = (storage: Storage, chave: string): unknown => {
  const cru = storage.getItem(chave);
  if (!cru) return null;
  try {
    return JSON.parse(cru);
  } catch {
    // Conteúdo inválido não deve impedir o chat de abrir: cai no padrão.
    return null;
  }
};

/**
 * Lê o layout do usuário. Na primeira leitura aproveita as chaves antigas
 * (globais) e as remove, para que não voltem a sobrepor o layout novo.
 */
export function lerLayoutSalvo(
  userId: string | null | undefined,
  viewport: Viewport,
  storage: Storage = localStorage
): ChatLayout {
  const salvo = lerJson(storage, chaveDoLayout(userId)) as Partial<ChatLayout> | null;
  if (salvo) return ajustarLayout(salvo, viewport);

  const size = lerJson(storage, CHAVES_ANTIGAS.size) as ChatLayout['size'] | null;
  const position = lerJson(storage, CHAVES_ANTIGAS.position) as ChatLayout['position'] | null;
  storage.removeItem(CHAVES_ANTIGAS.size);
  storage.removeItem(CHAVES_ANTIGAS.position);

  const layout = ajustarLayout({ size: size ?? undefined, position: position ?? undefined }, viewport);
  if (size || position) salvarLayout(userId, layout, storage);
  return layout;
}

export function salvarLayout(
  userId: string | null | undefined,
  layout: ChatLayout,
  storage: Storage = localStorage
): void {
  try {
    storage.setItem(chaveDoLayout(userId), JSON.stringify(layout));
  } catch (erro) {
    // Modo privado / cota cheia: perder a preferência de layout não pode
    // derrubar o chat.
    console.warn('Não foi possível salvar o layout do chat:', erro);
  }
}

export type BordaDeResize =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

interface ResizePelaBorda {
  borda: BordaDeResize;
  inicio: Pick<ChatLayout, 'position' | 'size'>;
  deslocamento: { dx: number; dy: number };
  viewport: Viewport;
}

/**
 * Novo tamanho/posição ao arrastar uma borda ou canto. Puxar pela esquerda ou
 * pelo topo cresce a janela para aquele lado: a borda oposta fica parada, e
 * quando a largura/altura encosta no mínimo (ou na lateral da tela) a borda
 * arrastada para de andar em vez de empurrar o popup.
 */
export function redimensionarPelaBorda({
  borda,
  inicio,
  deslocamento,
  viewport,
}: ResizePelaBorda): Pick<ChatLayout, 'position' | 'size'> {
  const { largura, altura } = LIMITES_DO_POPUP;
  let { x, y } = inicio.position;
  let { width, height } = inicio.size;

  if (borda.includes('right')) {
    width = limitar(inicio.size.width + deslocamento.dx, largura.min, Math.max(largura.min, viewport.width));
  }

  if (borda.includes('left')) {
    // Crescer para a esquerda só até a borda da tela (x = 0).
    const larguraMaxima = Math.max(largura.min, inicio.size.width + inicio.position.x);
    width = limitar(inicio.size.width - deslocamento.dx, largura.min, larguraMaxima);
    x = inicio.position.x + (inicio.size.width - width);
  }

  if (borda.includes('bottom')) {
    height = limitar(inicio.size.height + deslocamento.dy, altura.min, Math.max(altura.min, viewport.height));
  }

  if (borda.includes('top')) {
    const alturaMaxima = Math.max(altura.min, inicio.size.height + inicio.position.y);
    height = limitar(inicio.size.height - deslocamento.dy, altura.min, alturaMaxima);
    y = inicio.position.y + (inicio.size.height - height);
  }

  return { position: { x, y }, size: { width, height } };
}
